<template>
  <div
    class="slot-card"
    :class="{
      'slot-card--selected': selected,
      'slot-card--booked': isBooked,
      'slot-card--disabled': disabled,
      'slot-card--selectable': selectable && !disabled,
      'slot-card--compact': compact
    }"
    @click="onClick"
  >
    <div class="slot-card__time">
      <i
        v-if="selectable"
        :class="['fas me-2', selected ? 'fa-check-circle text-success' : 'fa-circle text-muted']"
      />
      <span class="slot-card__time-text">
        {{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
      </span>
    </div>

    <div
      v-if="!compact"
      class="slot-card__info"
    >
      <BBadge
        v-if="showAvailability"
        :variant="availabilityVariant"
        class="slot-card__badge"
      >
        {{ availabilityLabel }}
      </BBadge>

      <span
        v-if="showAppointmentCount && slot.active_appointments_count > 0"
        class="slot-card__count"
      >
        <i class="fas fa-users me-1" />
        {{ slot.active_appointments_count }}
      </span>
    </div>

    <div
      v-if="showActions && !selectable"
      class="slot-card__actions"
    >
      <BButton
        variant="outline-primary"
        size="sm"
        class="me-1"
        @click.stop="$emit('edit', slot)"
      >
        <i class="fas fa-edit" />
      </BButton>
      <BButton
        variant="outline-danger"
        size="sm"
        @click.stop="$emit('delete', slot)"
      >
        <i class="fas fa-trash" />
      </BButton>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SlotCard',

  props: {
    slot: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: false
    },
    showAvailability: {
      type: Boolean,
      default: true
    },
    showAppointmentCount: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    isBooked () {
      return this.slot.active_appointments_count > 0 || !this.slot.is_available
    },

    availabilityVariant () {
      if (!this.slot.is_available) return 'secondary'
      if (this.slot.active_appointments_count > 0) return 'warning'
      return 'success'
    },

    availabilityLabel () {
      if (!this.slot.is_available) return 'Unavailable'
      if (this.slot.active_appointments_count > 0) return 'Booked'
      return 'Available'
    }
  },

  methods: {
    formatTime (time) {
      if (!time) return ''
      // Convert 24h to 12h format
      const [hours, minutes] = time.substring(0, 5).split(':')
      const h = parseInt(hours, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${suffix}`
    },

    onClick () {
      if (this.disabled || !this.selectable) return
      this.$emit('select', this.slot)
    }
  }
}
</script>

<style lang="scss" scoped>
.slot-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &--selectable {
    cursor: pointer;

    &:hover:not(.slot-card--disabled) {
      border-color: #3b82f6;
      background: #f0f9ff;
    }
  }

  &--selected {
    border-color: #22c55e;
    background: #f0fdf4;
  }

  &--booked {
    background: #fefce8;
    border-color: #fbbf24;
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8fafc;
  }

  &--compact {
    padding: 8px 12px;
    margin-bottom: 4px;

    .slot-card__time-text {
      font-size: 0.85rem;
    }
  }

  &__time {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #1e293b;
  }

  &__time-text {
    font-size: 0.95rem;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__badge {
    font-size: 0.75rem;
    padding: 4px 8px;
  }

  &__count {
    font-size: 0.85rem;
    color: #64748b;
  }

  &__actions {
    display: flex;
    gap: 4px;
  }
}
</style>
